'use client'

import { memo } from 'react'
import { X, Check } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  onApply: (sortOrder: 'asc' | 'desc', selectedRoomTypes: string[]) => void
  onReset: () => void
  initialSortOrder?: 'asc' | 'desc'
  initialSelectedRoomTypes?: string[]
  roomTypes: { code: string; name: string }[]
}

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
}

const modalVariants = {
  hidden: { y: '100%', opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring' as const,
      damping: 25,
      stiffness: 300,
    },
  },
  exit: {
    y: '100%',
    opacity: 0,
    transition: {
      type: 'spring' as const,
      damping: 25,
      stiffness: 300,
    },
  },
}

function FilterModal({
  isOpen,
  onClose,
  onApply,
  onReset,
  initialSortOrder = 'desc',
  initialSelectedRoomTypes = [],
  roomTypes,
}: FilterModalProps) {
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder)
  const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>(initialSelectedRoomTypes)
  const [selectAll, setSelectAll] = useState(initialSelectedRoomTypes.length === roomTypes.length)
  const { locale } = useLocale()
  const t = getTranslation(locale)

  useEffect(() => {
    if (roomTypes.length > 0) {
      setSelectedRoomTypes(roomTypes.map((rt) => rt.code))
      setSelectAll(true)
    }
  }, [roomTypes])

  useEffect(() => {
    setSelectAll(selectedRoomTypes.length === roomTypes.length)
  }, [selectedRoomTypes, roomTypes])

  const handleRoomTypeToggle = (roomType: string) => {
    setSelectedRoomTypes((prev) => {
      if (prev.includes(roomType)) {
        return prev.filter((type) => type !== roomType)
      } else {
        return [...prev, roomType]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRoomTypes([])
    } else {
      setSelectedRoomTypes([...roomTypes.map((rt) => rt.code)])
    }
    setSelectAll(!selectAll)
  }

  const handleApply = () => {
    onApply(sortOrder, selectedRoomTypes)
  }

  const handleReset = () => {
    setSortOrder('desc')
    setSelectedRoomTypes(roomTypes.map((rt) => rt.code.trim()))
    setSelectAll(true)
    onReset()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/50"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={overlayVariants}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-y-auto bg-white"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              {/* Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-4">
                <div>
                  <h3 className="font-lora text-[18px] font-medium text-secondary-950">
                    {t('booking.filterTitle')}
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 transition-colors hover:text-gray-500"
                >
                  <X className="h-6 w-6 text-secondary-950" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                <div>
                  <h4 className="mb-3 text-base font-medium text-secondary-950">
                    {t('booking.price')}
                  </h4>
                  <div className="flex gap-5">
                    <div className="flex cursor-pointer items-center">
                      <label className="relative flex cursor-pointer items-center">
                        <input
                          type="radio"
                          name="priceSort"
                          checked={sortOrder === 'asc'}
                          className="peer hidden"
                          onChange={() => setSortOrder('asc')}
                        />
                        <div className="flex h-4 w-4 items-center justify-center rounded-full border-2 border-gray-400 peer-checked:border-primary-500 peer-checked:bg-primary-500">
                          <div className="h-3 w-3 rounded-full border-2 border-white"></div>
                        </div>
                        <span className="ml-2 font-lexend text-xs font-[350]">
                          {t('booking.priceAsc')}
                        </span>
                      </label>
                    </div>
                    <div className="flex cursor-pointer items-center">
                      <label className="relative flex cursor-pointer items-center">
                        <input
                          type="radio"
                          name="priceSort"
                          checked={sortOrder === 'desc'}
                          className="peer hidden"
                          onChange={() => setSortOrder('desc')}
                        />
                        <div className="flex h-4 w-4 items-center justify-center rounded-full border-2 border-gray-400 peer-checked:border-primary-500 peer-checked:bg-primary-500">
                          <div className="h-3 w-3 rounded-full border-2 border-white"></div>
                        </div>
                        <span className="ml-2 font-lexend text-xs font-[350]">
                          {t('booking.priceDesc')}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="my-4 h-[1px] w-full bg-[#F3F3F3]"></div>
                <div>
                  {/* Hạng phòng */}
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="font-lora text-base font-medium text-primary-500">
                      {t('booking.roomType')}
                    </h4>
                    <label className="flex cursor-pointer items-center gap-2">
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="relative h-5 w-5 cursor-pointer appearance-none rounded border-[1.5px] border-[#D1D5DB] transition-colors before:absolute before:left-[4px] before:top-[1px] checked:!border-primary-500 checked:!bg-primary-500 checked:before:opacity-100 focus:!border-primary-500 focus:outline-none focus:!ring-2 focus:!ring-primary-500 focus:ring-offset-2"
                          checked={selectAll}
                          onChange={handleSelectAll}
                        />
                      </div>
                      <span className="font-lexend text-xs font-[350] text-secondary-950">
                        {t('booking.selectAll')}
                      </span>
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {roomTypes.map((roomType, index) => {
                      const isSelected = selectedRoomTypes.includes(roomType.code)
                      return (
                        <button
                          key={index}
                          onClick={() => handleRoomTypeToggle(roomType.code)}
                          className={`group relative flex items-center rounded-[4px] px-4 py-2 text-sm ${
                            isSelected
                              ? 'border border-[#8B5E3C] bg-[#FDF8F4] text-[#8B5E3C]'
                              : 'border border-[#F3F3F3] bg-[#F3F3F3] text-[#111827]'
                          }`}
                        >
                          {isSelected && (
                            <div className="absolute left-[0px] top-[0px] h-5 w-5">
                              <div
                                className="absolute -left-[1px] -top-[1px] h-[25px] w-[25px]"
                                style={{ clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                              >
                                <div className="absolute h-full w-full rounded-[4px] bg-[#8B5E3C]" />
                                <div
                                  className="absolute h-[20px] w-[21px] bg-primary-500"
                                  style={{ clipPath: 'polygon(4px 0, 100% 0, 0 100%, 0 4px)' }}
                                >
                                  <Check className="absolute left-[3px] top-[3px] h-2.5 w-2.5 text-white" />
                                </div>
                              </div>
                            </div>
                          )}
                          <span className="font-lexend text-sm font-[400] leading-4">
                            {roomType.name}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
                <div className="my-4 h-[1px] w-full bg-[#F3F3F3]"></div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleReset}
                    className="flex-1 rounded-[4px] border border-primary-600 p-3 text-center font-lexend text-base font-[400] leading-4 text-primary-600 transition-colors hover:bg-primary-50"
                  >
                    {t('booking.reset')}
                  </button>
                  <button
                    onClick={handleApply}
                    className="flex-1 rounded-[4px] border border-primary-600 bg-primary-600 p-3 text-center font-lexend text-base font-[400] leading-4 text-white transition-colors hover:bg-primary-700"
                  >
                    {t('booking.apply')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

function arePropsEqual(prevProps: FilterModalProps, nextProps: FilterModalProps): boolean {
  // Compare basic props
  if (
    prevProps.isOpen !== nextProps.isOpen ||
    prevProps.initialSortOrder !== nextProps.initialSortOrder ||
    prevProps.roomTypes !== nextProps.roomTypes
  ) {
    return false
  }

  // Compare roomTypes
  const roomTypesEqual = prevProps.roomTypes.every(
    (type, index) =>
      type.code === nextProps.roomTypes[index].code && type.name === nextProps.roomTypes[index].name
  )
  if (!roomTypesEqual) return false

  // Compare initialSelectedRoomTypes
  const prevSelected = prevProps.initialSelectedRoomTypes || []
  const nextSelected = nextProps.initialSelectedRoomTypes || []

  if (prevSelected.length !== nextSelected.length) return false

  return prevSelected.every((type, index) => type === nextSelected[index])
}

export default memo(FilterModal, arePropsEqual)
